import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search } from 'lucide-react';
import SEO from '@/components/SEO';
import SectionLabel from '@/components/SectionLabel';
import ProfileCard from '@/components/ProfileCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REGIONS = [
  'All Regions',
  'Middle East & North Africa',
  'South Asia',
  'East Asia',
  'Southeast Asia & Pacific',
  'Sub-Saharan Africa',
  'North America',
  'Latin America & Caribbean',
  'Europe Western',
  'Europe Eastern & Nordic',
  'Central Asia & Caucasus',
  'Oceania & Australia',
];

const ProfilesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_profiles: 595, total_regions: 14 });

  // Filter states
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedRegion, setSelectedRegion] = useState(searchParams.get('region') || 'All Regions');
  const [selectedIndustry, setSelectedIndustry] = useState(searchParams.get('industry') || 'All Industries');
  const [selectedCountry, setSelectedCountry] = useState(searchParams.get('country') || 'All Countries');

  // Derived data
  const [industries, setIndustries] = useState(['All Industries']);
  const [countries, setCountries] = useState(['All Countries']);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profilesRes, statsRes, sectorsRes] = await Promise.all([
          axios.get(`${API_URL}/api/stories`),
          axios.get(`${API_URL}/api/stats`),
          axios.get(`${API_URL}/api/sectors`),
        ]);
        
        const profilesData = profilesRes.data;
        setProfiles(profilesData);
        setStats(statsRes.data);
        
        // Extract unique industries and countries
        const uniqueIndustries = ['All Industries', ...new Set(profilesData.map(p => p.sector).filter(Boolean))];
        const uniqueCountries = ['All Countries', ...new Set(profilesData.map(p => p.location).filter(Boolean))].sort();
        
        setIndustries(uniqueIndustries);
        setCountries(uniqueCountries);
      } catch (error) {
        console.error('Error fetching profiles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedRegion !== 'All Regions') params.set('region', selectedRegion);
    if (selectedIndustry !== 'All Industries') params.set('industry', selectedIndustry);
    if (selectedCountry !== 'All Countries') params.set('country', selectedCountry);
    setSearchParams(params);
  }, [searchQuery, selectedRegion, selectedIndustry, selectedCountry, setSearchParams]);

  // Filtered profiles
  const filteredProfiles = useMemo(() => {
    return profiles.filter((profile) => {
      const matchesSearch = !searchQuery || 
        profile.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        profile.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        profile.title?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesRegion = selectedRegion === 'All Regions' || profile.region === selectedRegion;
      const matchesIndustry = selectedIndustry === 'All Industries' || profile.sector === selectedIndustry;
      const matchesCountry = selectedCountry === 'All Countries' || profile.location === selectedCountry;
      
      return matchesSearch && matchesRegion && matchesIndustry && matchesCountry;
    });
  }, [profiles, searchQuery, selectedRegion, selectedIndustry, selectedCountry]);

  // Sort profiles: founder first, then featured, then by order
  const sortedProfiles = useMemo(() => {
    return [...filteredProfiles].sort((a, b) => {
      if (a.is_founder) return -1;
      if (b.is_founder) return 1;
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return (a.order || 0) - (b.order || 0);
    });
  }, [filteredProfiles]);

  return (
    <div data-testid="profiles-page" className="min-h-screen">
      <SEO 
        title="Women Leaders Database — 595+ Verified Profiles"
        description="Explore 595+ verified profiles of women leaders, CEOs, founders, and entrepreneurs across 7 continents and 30+ industries. Every fact sourced, every story authenticated."
        keywords={['women leaders directory', 'female CEO database', 'women founders list', 'women executives profiles', 'successful women entrepreneurs']}
        url="https://shebuiltthis.com/profiles"
        image="https://images.pexels.com/photos/7491147/pexels-photo-7491147.jpeg?auto=compress&cs=tinysrgb&w=1200"
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 bg-[#FAF9F6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-[12px] tracking-[0.3em] uppercase mb-4" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#C25E3E' }}>
              THE PROFILES &middot; {stats.total_profiles}+ VERIFIED
            </p>
            <h1 className="text-4xl lg:text-[72px] leading-[0.95] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: '#1A1A1A' }}>
              Every story<br />
              <span style={{ color: '#C25E3E' }}>authenticated.</span>
            </h1>
            <div className="w-16 h-[2px] mb-6 bg-[#C25E3E]" />
            <p className="text-lg max-w-xl leading-relaxed" style={{ fontFamily: "'Lora', serif", color: '#4A4A4A' }}>
              {stats.total_profiles}+ women across {stats.total_regions} regions and 7 continents. Every fact sourced. Every photograph verified.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Bar - Sticky */}
      <section className="sticky top-[108px] z-40 border-b border-[#1A1A1A]/10 bg-white/90 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1A1A1A]/30 group-focus-within:text-[#C25E3E] transition-colors" size={14} />
              <input
                type="text"
                placeholder="Search by name, title, company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-10 w-[240px] pl-10 pr-4 bg-[#FAF9F6] border border-[#1A1A1A]/10 text-[#1A1A1A] placeholder:text-[#1A1A1A]/30 text-[12px] tracking-wide focus:outline-none focus:border-[#C25E3E] transition-all duration-300"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="search-input"
              />
            </div>

            {/* Region Dropdown */}
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="h-10 px-4 pr-10 bg-[#FAF9F6] border border-[#1A1A1A]/10 text-[#1A1A1A] text-[12px] tracking-wide cursor-pointer focus:outline-none focus:border-[#C25E3E] transition-colors appearance-none"
              style={{ fontFamily: "'Outfit', sans-serif", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23C25E3E' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
              data-testid="region-filter"
            >
              {REGIONS.map((region) => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>

            {/* Industry Dropdown */}
            <select
              value={selectedIndustry}
              onChange={(e) => setSelectedIndustry(e.target.value)}
              className="h-10 px-4 pr-10 bg-transparent border border-[rgba(250,248,244,0.08)] text-[#1A1A1A] text-[12px] tracking-wide cursor-pointer focus:outline-none focus:border-[rgba(201,168,76,0.4)] transition-colors appearance-none"
              style={{ fontFamily: "'DM Mono', monospace", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23C9A84C' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
              data-testid="industry-filter"
            >
              {industries.map((industry) => (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </select>

            {/* Country Dropdown */}
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="h-10 px-4 pr-10 bg-[#FAF9F6] border border-[#1A1A1A]/10 text-[#1A1A1A] text-[12px] tracking-wide cursor-pointer focus:outline-none focus:border-[#C25E3E] transition-colors appearance-none"
              style={{ fontFamily: "'Outfit', sans-serif", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23C25E3E' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
              data-testid="country-filter"
            >
              {countries.map((country) => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>

            {/* Results count */}
            <div className="ml-auto">
              <p className="text-[12px] tracking-wide" style={{ fontFamily: "'Outfit', sans-serif", color: '#888888' }}>
                <span style={{ color: '#C25E3E', fontWeight: 600 }}>{sortedProfiles.length}</span> of {profiles.length}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Profiles Grid */}
      <section className="bg-[#FAF9F6] py-12 lg:py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i}>
                  <div className="aspect-[3/4] bg-[#F3EFE9] animate-pulse" />
                  <div className="mt-3 space-y-2">
                    <div className="h-4 bg-[#F3EFE9] w-3/4" />
                    <div className="h-3 bg-[#F3EFE9] w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : sortedProfiles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-lg" style={{ fontFamily: "'Lora', serif", color: '#4A4A4A' }}>
                No profiles found matching your criteria.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedRegion('All Regions');
                  setSelectedIndustry('All Industries');
                  setSelectedCountry('All Countries');
                }}
                className="mt-6 inline-flex items-center h-12 px-8 border-2 border-[#C25E3E] text-[#C25E3E] text-[12px] tracking-[0.15em] uppercase transition-all hover:bg-[#C25E3E] hover:text-white"
                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {sortedProfiles.map((profile, index) => (
                <ProfileCard
                  key={profile.slug}
                  profile={profile}
                  isFounder={profile.is_founder}
                  index={index}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProfilesPage;
